import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import BuildIcon from '@mui/icons-material/Build';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ImportedRowMatchWizard from './ImportedRowMatchWizard';
import ImportColumnValueMapping from './ImportColumnValueMapping';
import ImportAutoFillDialog, { ActiveFilter } from './ImportAutoFillDialog';
import { effectiveRate, formatRate, isBanded } from '../../utils/bandRate';

interface Option {
  id: string;
  name: string;
}

interface DistinctValueOption {
  rawText: string;
  rowCount: number;
}

interface BulkDryRunSampleRow {
  row: { businessName?: string; farmName?: string; fieldName?: string; productName?: string };
  wouldResolve: boolean;
}

interface BulkDryRunResult {
  totalMatchingRows: number;
  sample: BulkDryRunSampleRow[];
}

interface BulkJobStatus {
  id: string;
  status: 'Pending' | 'Running' | 'Completed' | 'Failed';
  totalMatched: number;
  processedCount: number;
  successCount: number;
  failureCount: number;
  errorSummary?: string;
}

// One column's raw text -> ImportedRowFilterDto property name (case-
// insensitive model binding on the API side) — the same seven columns
// GetDistinctValuesAsync supports. Region has no grid column of its own but
// is still filterable, per the original ask.
const FILTER_COLUMNS: { column: string; label: string }[] = [
  { column: 'BusinessName', label: 'Business' },
  { column: 'FarmName', label: 'Farm' },
  { column: 'FieldName', label: 'Field' },
  { column: 'ProductName', label: 'Product' },
  { column: 'ProductCategoryName', label: 'Category' },
  { column: 'GeneStack', label: 'Gene Stack' },
  { column: 'RegionName', label: 'Region' },
];

// Backs one filter-bar dropdown — options come from GetDistinctValuesAsync
// (the same raw-value+count data source the Column Mapping tab uses), typed
// search re-queries with a fresh `search` param rather than filtering
// client-side, since a large batch can have thousands of distinct values.
const FilterAutocomplete: React.FC<{
  label: string;
  column: string;
  batchId: string;
  authHeaders: () => Record<string, string>;
  value: string | null;
  onChange: (value: string | null) => void;
}> = ({ label, column, batchId, authHeaders, value, onChange }) => {
  const [options, setOptions] = useState<DistinctValueOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const params = new URLSearchParams({ column, pageSize: '25' });
      if (inputValue) params.set('search', inputValue);
      const res = await fetch(`/api/import-uploads/${batchId}/rows/distinct-values?${params.toString()}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOptions(data.items);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, column, batchId]);

  return (
    <Autocomplete<DistinctValueOption>
      size="small"
      sx={{ minWidth: 170 }}
      options={options}
      getOptionLabel={(o) => o.rawText}
      isOptionEqualToValue={(o, v) => o.rawText === v.rawText}
      filterOptions={(opts) => opts}
      value={value ? { rawText: value, rowCount: 0 } : null}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, v) => onChange(v?.rawText ?? null)}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.rawText}>
          {option.rawText}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            ({option.rowCount})
          </Typography>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} variant="standard" />}
    />
  );
};

// "existing" = existingId set, name matches an option; "new" = free-typed
// text with no matching option; "skip" = cleared entirely (only valid for
// optional fields — Farm/Field/Category/Gene Stack, not Business/Product/Unit).
interface FieldState {
  mode: 'existing' | 'new' | 'skip';
  existingId: string | null;
  name: string;
}

const emptySkip = (): FieldState => ({ mode: 'skip', existingId: null, name: '' });

interface GridRow {
  id: string;
  groupKey?: string;
  rawBusinessName?: string;
  rawFarmName?: string;
  rawFieldName?: string;
  rawRegionName?: string;
  rawProductName?: string;
  rawProductCategoryName?: string;
  rawGeneStack?: string;
  rawPests?: string;
  rawRate?: number;
  rawUnitName?: string;
  rawAreaHectares?: number;
  rawBandPercent?: number;
  // Boundary area of the suggested Field/Farm (or the matched Record's
  // stored MappedAreaHectares) — null until that feature has a boundary.
  mappedAreaHectares: number | null;
  rawEventDate?: string;
  isSeedRow: boolean;
  matchStatus?: string;
  matchedRecordId?: string;
  matchedFieldSeasonId?: string;
  // A field-season format row (CCA Supplementary Data): approving fills its
  // field's season, no Record — so it needs Farm/Field/Season instead of
  // Product/Unit/Operation Type.
  fieldSeasonOnly: boolean;
  error?: string;

  business: FieldState;
  farm: FieldState;
  field: FieldState;
  region: FieldState;
  product: FieldState;
  category: FieldState;
  geneStack: FieldState;
  unit: FieldState;
  operationTypeId: string | null;
  // Suggested Season (the file's own, else the batch default) — not shown
  // or edited in the grid, but sent on approve: without one the Record gets
  // no Season, so no in-season fallback date either — just the upload time.
  timePeriodId: string | null;
  pests: string[]; // resolved pest name strings (see ImportBatchReviewGrid's simplified pest model)
  // What approving fills in when the row has none of its own (see
  // ImportedRowMatchSuggestionsDto) — shown in the cell, never sent, so the
  // server applies it itself.
  fallbackRegion: Option | null;
  productOwnCategory: Option | null;
  productOwnGeneStack: Option | null;
}

interface Props {
  batch: { id: string; fileName: string };
  open: boolean;
  onClose: () => void;
  onApproved: () => void;
}

// A suggestion always wins, even with no raw text at all — Unit in
// particular is never stated per-row by Agworld (see AgworldFormatParser),
// so the ONLY way it's ever resolved is via a suggestion (Product's default
// rate unit, or an Auto Fill pre-set) with nothing in the raw column; gating
// on `raw` first silently discarded that suggestion and always showed the
// field as blank/skip regardless of what the server actually resolved.
const toFieldState = (raw: string | undefined, suggestion: Option | undefined): FieldState => {
  if (suggestion) return { mode: 'existing', existingId: suggestion.id, name: suggestion.name };
  if (raw) return { mode: 'new', existingId: null, name: raw };
  return emptySkip();
};

// Region (like Business) can only ever be an EXISTING record here — a
// Region needs drawn geometry, so there's no "create new" from raw text
// alone (see RegionAlias.cs). No suggestion just means skip, not "create."
const toExistingOnlyFieldState = (suggestion: Option | undefined): FieldState =>
  suggestion ? { mode: 'existing', existingId: suggestion.id, name: suggestion.name } : emptySkip();

const ImportBatchReviewGrid: React.FC<Props> = ({ batch, open, onClose, onApproved }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  // Admin-only density control — this grid packs a lot of columns (Business
  // through Operation Type) into one screen, so a smaller default plus a
  // +/- control beats forcing horizontal scrolling. `zoom` (not
  // `transform: scale`) scales layout AND fonts together without the
  // clipping/overlap `transform` would cause on a scrollable DataGrid.
  // Persisted per-browser so it doesn't reset every time the dialog reopens.
  const ZOOM_LEVELS = [70, 80, 90, 100, 110, 125];
  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const saved = Number(window.localStorage.getItem('importReviewGridZoom'));
    return ZOOM_LEVELS.includes(saved) ? saved : 85;
  });
  const setZoom = (level: number) => {
    setZoomLevel(level);
    window.localStorage.setItem('importReviewGridZoom', String(level));
  };
  const zoomOut = () => {
    const idx = ZOOM_LEVELS.filter((l) => l < zoomLevel).length - 1;
    if (idx >= 0) setZoom(ZOOM_LEVELS.filter((l) => l < zoomLevel)[idx]);
  };
  const zoomIn = () => {
    const next = ZOOM_LEVELS.find((l) => l > zoomLevel);
    if (next) setZoom(next);
  };

  const [rows, setRows] = useState<GridRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [wizardRowId, setWizardRowId] = useState<string | null>(null);

  // Unmatched first — that's the queue that actually needs review; with
  // thousands of rows, matched ones would otherwise bury it several pages
  // down instead of a tab switch away.
  const [statusTab, setStatusTab] = useState<'unmatched' | 'matched' | 'mapping'>('unmatched');
  const [rowCounts, setRowCounts] = useState({ matchedCount: 0, unmatchedCount: 0 });

  // Keyed by ImportedRowFilterDto property name (see FILTER_COLUMNS) — narrows
  // both the grid and the tab counts to rows with an exact (case-insensitive)
  // match on that column's raw text, sourced from the distinct-value picker
  // rather than free-typed search.
  const [filters, setFilters] = useState<Record<string, string | null>>({});
  const activeFilterCount = Object.values(filters).filter((v) => !!v).length;

  const [operationTypes, setOperationTypes] = useState<Option[]>([]);
  const [units, setUnits] = useState<Option[]>([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const res = await fetch('/api/operation-types', { headers: authHeaders() });
      if (res.ok) setOperationTypes((await res.json()).map((t: any) => ({ id: t.id, name: t.name })));
    })();
    (async () => {
      // Unit is REQUIRED to approve (unlike Farm/Field/Category/Gene Stack,
      // which are optional) but Agworld never states one per-row (see
      // AgworldFormatParser — "L or kg / ha" is ambiguous) — it only ever
      // gets suggested once some earlier row confirmed a default for that
      // exact product (Product.ExpectedRateUnitId). A freshly-mapped
      // product has no default yet, so this list backs an inline picker
      // instead of forcing every such row through the full wizard one at a
      // time just to set a unit.
      const res = await fetch('/api/units', { headers: authHeaders() });
      if (res.ok) setUnits((await res.json()).map((u: any) => ({ id: u.id, name: u.name })));
    })();
  }, [open, authHeaders]);

  const fetchCounts = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const res = await fetch(`/api/import-uploads/${batch.id}/rows/counts?${params.toString()}`, { headers: authHeaders() });
    if (res.ok) setRowCounts(await res.json());
  }, [batch.id, authHeaders, filters]);

  useEffect(() => {
    if (open) fetchCounts();
  }, [open, fetchCounts]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(paginationModel.page + 1),
        pageSize: String(paginationModel.pageSize),
        matched: String(statusTab === 'matched'),
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      const response = await fetch(`/api/import-uploads/${batch.id}/rows/match-suggestions?${params.toString()}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to load rows');
      const data = await response.json();
      setRowCount(data.totalCount);
      setRows(
        data.items.map((item: any): GridRow => {
          const r = item.row;
          const s = item.suggestions;
          const isSeedRow = !!(r.productCategoryName?.toLowerCase().includes('seed') || r.geneStack);
          const pestNames: string[] = (s.pests || []).map((p: any) => (p.species ? p.species.name : p.rawText));
          return {
            id: r.id,
            groupKey: r.groupKey,
            rawBusinessName: r.businessName,
            rawFarmName: r.farmName,
            rawFieldName: r.fieldName,
            rawRegionName: r.regionName,
            rawProductName: r.productName,
            rawProductCategoryName: r.productCategoryName,
            rawGeneStack: r.geneStack,
            rawPests: r.pests,
            rawRate: r.rate,
            rawUnitName: r.unitName,
            rawAreaHectares: r.areaHectares,
            rawBandPercent: r.bandPercent,
            mappedAreaHectares: s.mappedAreaHectares ?? null,
            rawEventDate: r.eventDate,
            isSeedRow,
            matchStatus: r.matchStatus,
            matchedRecordId: r.matchedRecordId,
            matchedFieldSeasonId: r.matchedFieldSeasonId,
            fieldSeasonOnly: !!s.fieldSeasonOnly,
            business: s.business
              ? { mode: 'existing', existingId: s.business.id, name: s.business.name }
              : emptySkip(),
            farm: toFieldState(r.farmName, s.farm),
            field: toFieldState(r.fieldName, s.field),
            region: toExistingOnlyFieldState(s.region),
            // Unlike the other fields, an unmatched Product is left BLANK
            // rather than pre-filled with the raw import text as a "new"
            // product to create — approving several rows at once with a
            // stale/misspelled raw name would otherwise silently create a
            // real Product record from it. The raw text is still visible in
            // the adjacent read-only Product column for reference.
            product: s.product ? { mode: 'existing', existingId: s.product.id, name: s.product.name } : emptySkip(),
            category: toFieldState(r.productCategoryName, s.productCategory),
            geneStack: toFieldState(r.geneStack, s.geneStack),
            unit: toFieldState(r.unitName, s.unit),
            operationTypeId: s.operationType?.id ?? null,
            timePeriodId: s.timePeriod?.id ?? null,
            pests: pestNames,
            fallbackRegion: s.fallbackRegion ?? null,
            productOwnCategory: s.productOwnCategory ?? null,
            productOwnGeneStack: s.productOwnGeneStack ?? null,
          };
        })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [batch.id, paginationModel, statusTab, authHeaders, filters]);

  useEffect(() => {
    if (open && statusTab !== 'mapping') fetchPage();
  }, [open, fetchPage, statusTab]);

  const handleTabChange = (tab: 'unmatched' | 'matched' | 'mapping') => {
    setStatusTab(tab);
    setSelectionModel([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (column: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
    setSelectionModel([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({});
    setSelectionModel([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const updateRow = (id: string, patch: Partial<GridRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const canApprove = (row: GridRow) => missingRequiredFields(row).length === 0;

  // Named per-field, not just a boolean — "No approvable rows selected"
  // alone gives no clue WHICH field is missing, and Unit in particular looks
  // identical to any other blank-but-optional cell (Farm/Field/Category/
  // Gene Stack) at a glance, so a generic message leaves the admin unable to
  // tell what actually needs fixing.
  const missingRequiredFields = (row: GridRow): string[] => {
    if (row.matchStatus === 'Matched') return ['already matched'];
    const missing: string[] = [];
    if (row.business.mode !== 'existing') missing.push('Business');
    if (row.fieldSeasonOnly) {
      if (row.farm.mode === 'skip') missing.push('Farm');
      if (row.field.mode === 'skip') missing.push('Field');
      if (!row.timePeriodId) missing.push('Season');
      return missing;
    }
    if (row.product.mode !== 'existing' && row.product.mode !== 'new') missing.push('Product');
    if (row.unit.mode !== 'existing' && row.unit.mode !== 'new') missing.push('Unit');
    if (!row.operationTypeId) missing.push('Operation Type');
    return missing;
  };

  // What a "good" record has: Product, Region, Category, Gene Stack, Unit.
  // Only Product and Unit are needed to approve (missingRequiredFields) —
  // a pending row missing any of them is tinted so it stands out. A
  // value the record gets anyway (the field/farm's region, the product's
  // own category or variety gene stack) counts as present.
  const missingDataFields = (row: GridRow): string[] => {
    if (row.matchStatus === 'Matched') return [];
    const missing: string[] = [];
    if (!row.fieldSeasonOnly && row.product.mode === 'skip') missing.push('Product');
    if (row.region.mode === 'skip' && !row.fallbackRegion) missing.push('Region');
    // A field-season row's product is the optional variety, and it has no rate.
    if (row.fieldSeasonOnly) return missing;
    if (row.category.mode === 'skip' && !row.productOwnCategory) missing.push('Category');
    if (row.geneStack.mode === 'skip' && !row.productOwnGeneStack) missing.push('Gene Stack');
    if (row.unit.mode === 'skip') missing.push('Unit');
    return missing;
  };
  const missingDataRowCount = rows.filter((r) => missingDataFields(r).length > 0).length;

  const buildResolveRequest = (row: GridRow) => ({
    business: { existingId: row.business.existingId },
    farm: row.farm.mode === 'skip' ? null : row.farm.mode === 'existing' ? { existingId: row.farm.existingId } : { newName: row.farm.name },
    field: row.field.mode === 'skip' ? null : row.field.mode === 'existing' ? { existingId: row.field.existingId } : { newName: row.field.name },
    timePeriod: row.timePeriodId ? { existingId: row.timePeriodId } : null,
    product: {
      existingId: row.product.mode === 'existing' ? row.product.existingId : null,
      newName: row.product.mode === 'new' ? row.product.name : null,
      category: row.category.mode === 'skip' ? null : row.category.mode === 'existing' ? { existingId: row.category.existingId } : { newName: row.category.name },
      geneStack: !row.isSeedRow || row.geneStack.mode === 'skip' ? null : row.geneStack.mode === 'existing' ? { existingId: row.geneStack.existingId } : { newName: row.geneStack.name },
    },
    // A field-season row has no rate or operation.
    ...(row.fieldSeasonOnly
      ? {}
      : {
          unit: row.unit.mode === 'existing' ? { existingId: row.unit.existingId } : { newName: row.unit.name },
          operationTypeId: row.operationTypeId,
        }),
    regionId: row.region.mode === 'existing' ? row.region.existingId : null,
    applyToSimilar: false,
    pests: row.pests.map((name) => ({ rawText: name, newName: name, skip: false })),
  });

  const handleApprove = async () => {
    const selectedIds = new Set(selectionModel.map(String));
    const selected = rows.filter((r) => selectedIds.has(r.id));
    const candidates = selected.filter(canApprove);
    if (candidates.length === 0) {
      // Tally which field is missing across the selected rows, not already-
      // matched ones, so the message says exactly what to fix.
      const missingCounts = new Map<string, number>();
      for (const row of selected) {
        for (const field of missingRequiredFields(row)) {
          if (field === 'already matched') continue;
          missingCounts.set(field, (missingCounts.get(field) || 0) + 1);
        }
      }
      const detail = Array.from(missingCounts.entries())
        .map(([field, count]) => `${field} missing on ${count} row(s)`)
        .join(', ');
      setError(detail ? `No approvable rows selected — ${detail}.` : 'No approvable rows selected — the selected row(s) are already matched.');
      return;
    }

    setApproving(true);
    setError(null);
    try {
      const response = await fetch('/api/import-uploads/match-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(candidates.map((r) => ({ rowId: r.id, request: buildResolveRequest(r) }))),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Bulk approve failed');

      const errorsByRow = new Map<string, string>();
      for (const result of data.results) {
        if (!result.success) errorsByRow.set(result.rowId, result.error);
      }
      setSelectionModel([]);
      // Approved rows no longer belong on the Unmatched tab — reload from
      // the server (rather than just patching matchStatus locally) so the
      // list and its counts stay consistent with the now-server-filtered
      // view, then re-apply any per-row error messages onto whichever of
      // those rows are still present (failed ones stay Unmatched).
      await Promise.all([fetchPage(), fetchCounts()]);
      if (errorsByRow.size > 0) {
        setRows((prev) => prev.map((r) => (errorsByRow.has(r.id) ? { ...r, error: errorsByRow.get(r.id) } : r)));
      }
      if (data.failureCount === 0) {
        onApproved();
      } else {
        setError(`${data.successCount} row(s) approved, ${data.failureCount} failed — see row error tooltips.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setApproving(false);
    }
  };

  const buildFiltersPayload = () =>
    Object.fromEntries(Object.entries(filters).filter(([, v]) => !!v));

  const activeFilters: ActiveFilter[] = FILTER_COLUMNS
    .filter(({ column }) => !!filters[column])
    .map(({ column, label }) => ({ column, label, value: filters[column]! }));

  const [autoFillOpen, setAutoFillOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDryRun, setBulkDryRun] = useState<BulkDryRunResult | null>(null);
  const [bulkDryRunLoading, setBulkDryRunLoading] = useState(false);
  const [bulkJob, setBulkJob] = useState<BulkJobStatus | null>(null);
  const [bulkCommitting, setBulkCommitting] = useState(false);
  const bulkPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const openBulkApproveDialog = async () => {
    setBulkDialogOpen(true);
    setBulkDryRun(null);
    setBulkJob(null);
    setBulkDryRunLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/import-uploads/${batch.id}/rows/approve-by-filter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ filters: buildFiltersPayload(), dryRun: true }),
      });
      if (!response.ok) throw new Error('Failed to preview the filtered set');
      setBulkDryRun(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setBulkDialogOpen(false);
    } finally {
      setBulkDryRunLoading(false);
    }
  };

  const closeBulkApproveDialog = () => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    setBulkDialogOpen(false);
    setBulkDryRun(null);
    setBulkJob(null);
  };

  const pollBulkJob = (jobId: string) => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      const res = await fetch(`/api/import-uploads/bulk-approve-jobs/${jobId}`, { headers: authHeaders() });
      if (!res.ok) return;
      const job: BulkJobStatus = await res.json();
      setBulkJob(job);
      if (job.status === 'Completed' || job.status === 'Failed') {
        if (bulkPollRef.current) clearInterval(bulkPollRef.current);
        await Promise.all([fetchPage(), fetchCounts()]);
        if (job.status === 'Completed') onApproved();
      }
    }, 2000);
  };

  const handleCommitBulkApprove = async () => {
    setBulkCommitting(true);
    setError(null);
    try {
      const response = await fetch(`/api/import-uploads/${batch.id}/rows/approve-by-filter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ filters: buildFiltersPayload(), dryRun: false }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to queue bulk approve');
      setBulkJob({ id: data.jobId, status: 'Pending', totalMatched: bulkDryRun?.totalMatchingRows ?? 0, processedCount: 0, successCount: 0, failureCount: 0 });
      pollBulkJob(data.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setBulkCommitting(false);
    }
  };

  useEffect(() => () => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
  }, []);

  // Shows what a field actually resolved/will resolve to — not just the raw
  // import text — so both tabs answer "what did this become" without
  // leaving the screen: 'existing' shows the real matched record's name
  // (plus the raw text underneath only when it actually differs from it,
  // so a straight match doesn't repeat itself); 'new' flags that approving
  // will create a brand-new record with that name; 'skip' is a plain dash.
  //
  // `fallback` is a value the record gets anyway when the row has none (see
  // GridRow.fallbackRegion) — shown with where it comes from.
  const renderMatchCell = (fieldState: FieldState, rawText?: string, fallback?: { option: Option | null; source: string }) => {
    if (fieldState.mode === 'skip' && fallback?.option) {
      return (
        <Box sx={{ lineHeight: 1.25, py: 0.5 }}>
          <Typography variant="body2">{fallback.option.name}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">{fallback.source}</Typography>
        </Box>
      );
    }
    if (fieldState.mode === 'skip' && rawText) {
      // Text the file states that matched nothing (Region, Product) — map
      // it on the Column Mapping tab.
      return (
        <Box sx={{ lineHeight: 1.25, py: 0.5 }}>
          <Typography variant="body2" color="text.secondary">{rawText}</Typography>
          <Typography variant="caption" color="warning.main" component="div">no match</Typography>
        </Box>
      );
    }
    if (fieldState.mode === 'skip') {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    if (fieldState.mode === 'new') {
      return (
        <Tooltip title="No existing match — approving will create a new record with this name">
          <Chip size="small" variant="outlined" color="warning" label={fieldState.name || rawText || '-'} />
        </Tooltip>
      );
    }
    const resolvedName = fieldState.name;
    const differs = !!rawText && !!resolvedName && rawText.trim().toLowerCase() !== resolvedName.trim().toLowerCase();
    return (
      <Box sx={{ lineHeight: 1.25, py: 0.5 }}>
        <Typography variant="body2">{resolvedName || '-'}</Typography>
        {differs && (
          <Typography variant="caption" color="text.secondary" component="div">
            imported as &quot;{rawText}&quot;
          </Typography>
        )}
      </Box>
    );
  };

  const columns: GridColDef<GridRow>[] = useMemo(
    () => [
      {
        // Leftmost (not after every other column) so it's always visible
        // without scrolling right — MUI X Community DataGrid (this app's
        // tier) doesn't support pinned columns, so position is the only way
        // to guarantee that on a wide row.
        field: 'actions', headerName: 'Edit', width: 60, minWidth: 50, resizable: true, sortable: false,
        renderCell: ({ row }) => (
          <Tooltip title="Open full wizard for this row">
            <IconButton size="small" onClick={() => setWizardRowId(row.id)}><BuildIcon fontSize="small" /></IconButton>
          </Tooltip>
        ),
      },
      {
        field: 'status', headerName: 'Status', width: 90, minWidth: 70, resizable: true, sortable: false,
        renderCell: ({ row }) =>
          row.matchStatus === 'Matched' ? (
            <Tooltip title={row.matchedRecordId ? `Record ${row.matchedRecordId}` : row.matchedFieldSeasonId ? 'Filled its field season' : 'Matched'}>
              <Chip label="Matched" size="small" color="success" />
            </Tooltip>
          ) : row.error ? (
            <Tooltip title={row.error}><Chip label="Error" size="small" color="error" /></Tooltip>
          ) : missingDataFields(row).length > 0 ? (
            <Tooltip title={`Missing ${missingDataFields(row).join(', ')}`}>
              <Chip label="Pending" size="small" color="warning" variant="outlined" />
            </Tooltip>
          ) : (
            <Chip label="Pending" size="small" />
          ),
      },
      {
        field: 'rawEventDate', headerName: 'Date', width: 100, minWidth: 80, resizable: true, sortable: false,
        // Summary formats (CCA Summary, CCA Market Audit) carry no date at
        // all — those rows show '-' here and fall back to the matched
        // Product's Default Use Date in the Season (else 1 Jan in it), not this.
        valueGetter: ({ row }) => (row.rawEventDate ? new Date(row.rawEventDate).toLocaleDateString() : '-'),
      },
      {
        field: 'groupKey', headerName: 'Group', width: 90, minWidth: 70, resizable: true, sortable: false,
        renderCell: ({ row }) =>
          row.groupKey ? (
            <Tooltip title="Rows sharing this Group value merge into one Record (with one product line each) on approval">
              <Chip label={row.groupKey} size="small" variant="outlined" />
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        field: 'rawBusinessName', headerName: 'Business', width: 150, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.business, row.rawBusinessName),
      },
      {
        field: 'rawFarmName', headerName: 'Farm', width: 140, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.farm, row.rawFarmName),
      },
      {
        field: 'rawFieldName', headerName: 'Field', width: 140, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.field, row.rawFieldName),
      },
      {
        field: 'rawRegionName', headerName: 'Region', width: 140, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.region, row.rawRegionName, { option: row.fallbackRegion, source: "field/farm's region" }),
      },
      {
        field: 'rawProductName', headerName: 'Product', width: 200, minWidth: 120, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.product, row.rawProductName),
      },
      {
        field: 'rawProductCategoryName', headerName: 'Category', width: 140, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.category, row.rawProductCategoryName, { option: row.productOwnCategory, source: "product's category" }),
      },
      {
        field: 'rawGeneStack', headerName: 'Gene Stack', width: 150, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => renderMatchCell(row.geneStack, row.rawGeneStack, { option: row.productOwnGeneStack, source: "variety's gene stack" }),
      },
      {
        field: 'pests', headerName: 'Pests', width: 160, minWidth: 100, resizable: true, sortable: false,
        renderCell: ({ row }) => (
          <Autocomplete
            multiple size="small" fullWidth freeSolo disabled={row.matchStatus === 'Matched'}
            options={[]}
            value={row.pests}
            onChange={(_, value) => updateRow(row.id, { pests: value as string[] })}
            renderTags={(value, getTagProps) => value.map((v, i) => <Chip {...getTagProps({ index: i })} key={v} label={v} size="small" />)}
            renderInput={(params) => <TextField {...params} variant="standard" placeholder={row.rawPests ? 'Edit pests' : '—'} />}
          />
        ),
      },
      {
        // The application rate, with what the band makes it over the whole area.
        field: 'rawRate', headerName: 'Rate', width: 90, minWidth: 55, resizable: true, sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ lineHeight: 1.25, py: 0.5 }}>
            <Typography variant="body2">{row.rawRate ?? '-'}</Typography>
            {row.rawRate != null && isBanded(row.rawBandPercent) && (
              <Typography variant="caption" color="text.secondary" component="div">{formatRate(effectiveRate(row.rawRate, row.rawBandPercent))} effective</Typography>
            )}
          </Box>
        ),
      },
      {
        // The file's own stated hectares (becomes Record.AreaHectares) with
        // the matched Field/Farm's mapped boundary area beneath it — both
        // are stored on the Record for reconciliation; a >10% gap is
        // flagged so a wrong field match stands out before approving.
        field: 'rawAreaHectares', headerName: 'Ha', width: 115, minWidth: 80, resizable: true, sortable: false,
        renderCell: ({ row }) => {
          const stated = row.rawAreaHectares;
          const mapped = row.mappedAreaHectares;
          const banded = isBanded(row.rawBandPercent);
          const mismatch = stated != null && mapped != null && mapped > 0 && Math.abs(stated - mapped) / mapped > 0.1;
          return (
            <Box sx={{ lineHeight: 1.25, py: 0.5 }}>
              <Typography variant="body2">{stated ?? '-'}</Typography>
              {banded && (
                <Typography variant="caption" color="text.secondary" component="div">{row.rawBandPercent}% band</Typography>
              )}
              {mapped != null && (
                <Tooltip title={mismatch ? 'Differs from the mapped boundary area by more than 10% — check the field match' : 'Mapped boundary area of the matched field/farm'}>
                  <Typography variant="caption" color={mismatch ? 'warning.main' : 'text.secondary'} component="div">
                    mapped {mapped}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        // Unlike Farm/Field/Category/Gene Stack, Unit is REQUIRED to
        // approve (see canApprove) — but Agworld never states one per-row
        // (AgworldFormatParser: "L or kg / ha" is ambiguous), so a freshly-
        // mapped product with no ExpectedRateUnitId default yet lands here
        // with nothing suggested. Editable inline (rather than read-only
        // like the other match cells) so that doesn't force a trip through
        // the full wizard just to pick a unit, and a missing-but-required
        // value is flagged red rather than looking like any other blank
        // optional field.
        field: 'rawUnitName', headerName: 'Unit', width: 150, minWidth: 100, resizable: true, sortable: false,
        renderCell: ({ row }) =>
          row.fieldSeasonOnly ? (
            'n/a'
          ) : row.matchStatus === 'Matched' ? (
            renderMatchCell(row.unit, row.rawUnitName)
          ) : (
            <Autocomplete
              size="small" fullWidth
              options={units}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={row.unit.mode === 'existing' ? units.find((u) => u.id === row.unit.existingId) || null : null}
              onChange={(_, value) => updateRow(row.id, { unit: value ? { mode: 'existing', existingId: value.id, name: value.name } : emptySkip() })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder="Required"
                  error={row.unit.mode === 'skip'}
                  helperText={row.unit.mode === 'skip' ? 'Required to approve' : undefined}
                />
              )}
            />
          ),
      },
      {
        field: 'operationTypeId', headerName: 'Operation Type', width: 140, minWidth: 90, resizable: true, sortable: false,
        renderCell: ({ row }) => row.fieldSeasonOnly ? 'n/a' : (
          <Select
            size="small" fullWidth variant="standard" disabled={row.matchStatus === 'Matched'}
            value={row.operationTypeId || ''}
            onChange={(e) => updateRow(row.id, { operationTypeId: e.target.value || null })}
            displayEmpty
          >
            <MenuItem value=""><em>—</em></MenuItem>
            {operationTypes.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </Select>
        ),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [operationTypes, units]
  );

  const wizardRow = rows.find((r) => r.id === wizardRowId);

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
        <Toolbar>
          <Typography sx={{ ml: 1, flex: 1 }} variant="h6">
            Review Import — {batch.fileName}
          </Typography>
          {statusTab === 'unmatched' && (
            <Tooltip title="Set one value (e.g. Unit, Region) on every row matching the filters, or on every row missing it">
              <Button variant="outlined" onClick={() => setAutoFillOpen(true)} sx={{ mr: 2 }}>
                Auto Fill
              </Button>
            </Tooltip>
          )}
          {statusTab === 'unmatched' && activeFilterCount > 0 && (
            <Tooltip title="Resolve every unmatched row matching the current filters, using each row's own suggested match — not just what's selected on this page">
              <span>
                <Button variant="outlined" onClick={openBulkApproveDialog} sx={{ mr: 2 }}>
                  Approve All Filtered
                </Button>
              </span>
            </Tooltip>
          )}
          {statusTab === 'unmatched' && (
            <Button
              variant="contained"
              disabled={approving || selectionModel.length === 0}
              onClick={handleApprove}
              sx={{ mr: 2 }}
            >
              {approving ? 'Approving...' : `Approve Selected (${selectionModel.length})`}
            </Button>
          )}
          <Tooltip title="Decrease grid size">
            <span>
              <IconButton size="small" onClick={zoomOut} disabled={zoomLevel <= ZOOM_LEVELS[0]}><ZoomOutIcon fontSize="small" /></IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'center' }}>{zoomLevel}%</Typography>
          <Tooltip title="Increase grid size">
            <span>
              <IconButton size="small" onClick={zoomIn} disabled={zoomLevel >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}><ZoomInIcon fontSize="small" /></IconButton>
            </span>
          </Tooltip>
          <IconButton edge="end" onClick={onClose} sx={{ ml: 1 }}><CloseIcon /></IconButton>
        </Toolbar>
        <Tabs value={statusTab} onChange={(_, v) => handleTabChange(v)} sx={{ px: 2 }}>
          <Tab value="unmatched" label={`Unmatched (${rowCounts.unmatchedCount})`} />
          <Tab value="matched" label={`Matched (${rowCounts.matchedCount})`} />
          <Tab value="mapping" label="Column Mapping" />
        </Tabs>
      </AppBar>

      {/* zoom (not transform: scale) so the DataGrid actually reflows at the
          scaled size — Chromium/Edge/Safari 16.4+ recalculate layout under
          zoom, unlike a visual-only transform, which would otherwise clip or
          misalign the grid's own scrollbars. Admin-only screen, so the lack
          of Firefox support isn't a blocker; content still renders at 100%
          there, just without the density control taking effect. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', zoom: `${zoomLevel}%` }}>
        {statusTab !== 'mapping' && (
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1, rowGap: 1 }}>
            {FILTER_COLUMNS.map(({ column, label }) => (
              <FilterAutocomplete
                key={column}
                label={label}
                column={column}
                batchId={batch.id}
                authHeaders={authHeaders}
                value={filters[column] || null}
                onChange={(value) => handleFilterChange(column, value)}
              />
            ))}
            {activeFilterCount > 0 && (
              <Tooltip title="Clear filters">
                <IconButton size="small" onClick={clearFilters}>
                  <FilterAltOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {statusTab === 'unmatched' && missingDataRowCount > 0 && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 'auto !important' }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.25) }} />
                <Typography variant="caption" color="text.secondary">
                  {missingDataRowCount} row(s) on this page missing Product, Region, Category, Gene Stack or Unit
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {error && (
          <Alert severity="warning" sx={{ m: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {statusTab === 'mapping' ? (
          <ImportColumnValueMapping batchId={batch.id} onMappingApplied={fetchCounts} />
        ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0, p: 1 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={() => 56}
                checkboxSelection={statusTab === 'unmatched'}
                isRowSelectable={({ row }) => row.matchStatus !== 'Matched'}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={setSelectionModel}
                getRowClassName={({ row }) => (missingDataFields(row).length > 0 ? 'row-missing-data' : '')}
                // The MIT DataGrid throws on a pageSize over 100, so paging
                // is the TablePagination below. In server mode the grid
                // shows every row it's given, whatever the page size.
                paginationMode="server"
                hideFooterPagination
                loading={loading}
                disableColumnMenu
                sx={{
                  height: '100%',
                  border: 0,
                  '& .row-missing-data': {
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.16) },
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.22) },
                  },
                }}
              />
            </Box>
            <TablePagination
              component="div"
              count={rowCount}
              page={paginationModel.page}
              rowsPerPage={paginationModel.pageSize}
              rowsPerPageOptions={[25, 50, 100, 500, 1000]}
              onPageChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page }))}
              onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: Number(e.target.value) })}
              sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}
            />
          </>
        )}
      </Box>

      {wizardRow && (
        <ImportedRowMatchWizard
          row={{
            id: wizardRow.id,
            businessName: wizardRow.rawBusinessName,
            farmName: wizardRow.rawFarmName,
            fieldName: wizardRow.rawFieldName,
            productName: wizardRow.rawProductName,
            productCategoryName: wizardRow.rawProductCategoryName,
            geneStack: wizardRow.rawGeneStack,
            rate: wizardRow.rawRate,
            unitName: wizardRow.rawUnitName,
            areaHectares: wizardRow.rawAreaHectares,
            bandPercent: wizardRow.rawBandPercent,
            eventDate: wizardRow.rawEventDate,
          }}
          open={!!wizardRow}
          onClose={() => setWizardRowId(null)}
          onMatched={() => { setWizardRowId(null); fetchPage(); fetchCounts(); }}
        />
      )}

      <ImportAutoFillDialog
        batchId={batch.id}
        filters={activeFilters}
        open={autoFillOpen}
        onClose={() => setAutoFillOpen(false)}
        onApplied={() => { fetchPage(); fetchCounts(); }}
      />

      <Dialog open={bulkDialogOpen} onClose={bulkJob?.status === 'Running' || bulkJob?.status === 'Pending' ? undefined : closeBulkApproveDialog} maxWidth="md" fullWidth>
        <DialogTitle>Approve All Filtered Rows</DialogTitle>
        <DialogContent>
          {bulkDryRunLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : bulkJob ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Status: <strong>{bulkJob.status}</strong>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={bulkJob.totalMatched > 0 ? (bulkJob.processedCount / bulkJob.totalMatched) * 100 : 0}
                sx={{ mb: 1, height: 8, borderRadius: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {bulkJob.processedCount} of {bulkJob.totalMatched} processed — {bulkJob.successCount} approved, {bulkJob.failureCount} could not be auto-resolved
              </Typography>
              {bulkJob.status === 'Failed' && bulkJob.errorSummary && (
                <Alert severity="error" sx={{ mt: 2 }}>{bulkJob.errorSummary}</Alert>
              )}
              {bulkJob.status === 'Completed' && (
                <Alert severity="success" sx={{ mt: 2 }}>Done — the grid has been refreshed.</Alert>
              )}
            </Box>
          ) : bulkDryRun ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This will attempt to resolve <strong>{bulkDryRun.totalMatchingRows}</strong> unmatched row(s) matching
                the current filters, using each row's own suggested match (alias or fuzzy match) — rows without a
                complete suggestion (Business, Product, Unit, Operation Type) are left unmatched for manual review.
              </Typography>
              {bulkDryRun.sample.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Preview (first {bulkDryRun.sample.length} rows)</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Business</TableCell>
                        <TableCell>Farm</TableCell>
                        <TableCell>Field</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Outcome</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkDryRun.sample.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>{s.row.businessName || '-'}</TableCell>
                          <TableCell>{s.row.farmName || '-'}</TableCell>
                          <TableCell>{s.row.fieldName || '-'}</TableCell>
                          <TableCell>{s.row.productName || '-'}</TableCell>
                          <TableCell>
                            {s.wouldResolve ? (
                              <Chip size="small" color="success" label="Will approve" />
                            ) : (
                              <Chip size="small" variant="outlined" label="Needs review" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {bulkJob ? (
            <Button onClick={closeBulkApproveDialog} disabled={bulkJob.status === 'Pending' || bulkJob.status === 'Running'}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={closeBulkApproveDialog}>Cancel</Button>
              <Button
                variant="contained"
                disabled={bulkDryRunLoading || bulkCommitting || !bulkDryRun || bulkDryRun.totalMatchingRows === 0}
                onClick={handleCommitBulkApprove}
              >
                {bulkCommitting ? 'Starting...' : `Approve ${bulkDryRun?.totalMatchingRows ?? ''} Row(s)`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ImportBatchReviewGrid;
